import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { Menu, X, Rocket, User, LogOut, Shield } from 'lucide-react';
import LanguageSwitcher from './LanguageSwitcher';
import gsap from 'gsap';

const Header = () => {
  const { t } = useTranslation();
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const headerRef = useRef(null);

  useEffect(() => {
    gsap.fromTo(headerRef.current,
      { y: -100, opacity: 0 },
      { y: 0, opacity: 1, duration: 1, ease: 'power3.out' }
    );
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
    setIsMenuOpen(false);
  };

  return (
    <header ref={headerRef} className="fixed top-0 w-full z-50 backdrop-blur-md bg-background/80 border-b border-white/5 h-16 sm:h-20 transition-all duration-300">
      <div className="container mx-auto px-4 h-full flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 group">
          <div className="relative">
            <div className="absolute inset-0 bg-primary/20 blur-lg rounded-full group-hover:bg-primary/40 transition-all duration-300"></div>
            <Rocket className="w-6 h-6 sm:w-8 sm:h-8 text-primary relative z-10 transform group-hover:-translate-y-1 transition-transform duration-300" />
          </div>
          <span className="text-xl sm:text-2xl font-bold font-display tracking-tight text-white group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-primary group-hover:to-secondary transition-all duration-300">
            IdeaPlatform
          </span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          <Link to="/" className="text-sm font-medium text-slate-400 hover:text-white transition-colors duration-200">
            {t('nav.home')}
          </Link>

          {user ? (
            <>
              <Link to="/submit" className="text-sm font-medium text-slate-400 hover:text-white transition-colors duration-200">
                {t('nav.submit')}
              </Link>
              <div className="flex items-center gap-4 pl-4 border-l border-white/10">
                <span className="flex items-center gap-2 text-sm text-slate-200">
                  <div className="w-8 h-8 rounded-full bg-surface border border-white/10 flex items-center justify-center">
                    <User className="w-4 h-4 text-primary" />
                  </div>
                  <span className="hidden lg:inline">{user.name}</span>
                </span>
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 hover:bg-white/5 hover:border-red-500/50 hover:text-red-400 text-slate-400 text-sm transition-all duration-200 group"
                >
                  <LogOut className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                  {t('nav.logout')}
                </button>
              </div>
            </>
          ) : (
            <div className="flex items-center gap-4">
              <Link to="/login" className="text-sm font-medium text-slate-400 hover:text-white transition-colors">
                {t('nav.login')}
              </Link>
              <Link
                to="/signup"
                className="px-6 py-2.5 rounded-full bg-gradient-to-r from-primary to-secondary text-white text-sm font-bold shadow-lg shadow-primary/20 hover:shadow-primary/40 hover:scale-105 active:scale-95 transition-all duration-300"
              >
                {t('nav.signup')}
              </Link>
            </div>
          )}

          <LanguageSwitcher />
          <Link to="/admin/login" className="p-2 hover:bg-white/5 rounded-full transition-colors text-slate-500 hover:text-white list-none">
            <Shield className="w-5 h-5" />
          </Link>
        </nav>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden p-2 text-slate-300 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Nav Overlay */}
      <div className={`md:hidden absolute top-full left-0 w-full bg-background border-b border-white/10 transition-all duration-300 overflow-hidden ${isMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="p-4 flex flex-col gap-4">
          <Link to="/" className="p-2 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors" onClick={() => setIsMenuOpen(false)}>
            Home
          </Link>
          {user ? (
            <>
              <Link to="/submit" className="p-2 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors" onClick={() => setIsMenuOpen(false)}>
                Submit Idea
              </Link>
              <div className="h-px bg-white/10 my-2"></div>
              <div className="flex items-center gap-3 p-2">
                <div className="w-8 h-8 rounded-full bg-surface border border-white/10 flex items-center justify-center">
                  <User className="w-4 h-4 text-primary" />
                </div>
                <span className="text-slate-200">{user.name}</span>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors w-full"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="p-2 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors" onClick={() => setIsMenuOpen(false)}>
                Login
              </Link>
              <Link to="/signup" className="p-2 text-center bg-primary rounded-lg text-white font-medium" onClick={() => setIsMenuOpen(false)}>
                Sign Up
              </Link>
            </>
          )}
          <Link to="/admin/login" className="flex items-center gap-2 p-2 text-slate-500 hover:text-white hover:bg-white/5 rounded-lg transition-colors" onClick={() => setIsMenuOpen(false)}>
            <Shield className="w-4 h-4" /> Admin
          </Link>
        </div>
      </div>
    </header>
  );
};

export default Header;
